/**
 * PetPolicyHero — Weidner Via6 page hero for /pet-policy.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Pet policy hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PetPolicyHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="petpolicyhero.title"
        bgPath="petpolicyhero.bg_image"
        bgDef={bgDef}
        defaultTitle="Pet Policy"
        keywords={['pet', 'pets', 'dog', 'cat', 'park']}
        pageHint="petpolicyhero"
        altText="Pet policy hero image"
      />

      <RichTextContent
        path="petpolicyhero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="petpolicyhero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyHero',
  displayName: 'Pet Policy Hero',
  tokens: {
    'petpolicyhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Pet Policy',
    },
  },
  slotGroups: {
    'petpolicyhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Pet policy hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PetPolicyHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyHero;
